import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Consent } from '@models/consent/consent';
import { Action, createSelector, NgxsAfterBootstrap, State, StateContext } from '@ngxs/store';
import { iif, insertItem, updateItem } from '@ngxs/store/operators';
import { SetConsent, SetConsents } from '@store/consents/consents.actions';
import { fromEvent } from 'rxjs';

export type ConsentsStateModel = Consent[];

@State<ConsentsStateModel>({
  name: 'consents',
  defaults: [],
})
@Injectable()
export class ConsentsState implements NgxsAfterBootstrap {
  static consent(key: string): any {
    return createSelector([ConsentsState], (state: ConsentsStateModel) => {
      return state.find((consent) => consent.key === key);
    });
  }

  static hasConsent(key: string): any {
    return createSelector([ConsentsState], (state: ConsentsStateModel) => {
      return !!state.find((consent) => consent.key === key);
    });
  }

  constructor(@Inject(DOCUMENT) private readonly document: any) {}

  ngxsAfterBootstrap(ctx?: StateContext<any>): void {
    fromEvent(this.document, 'WizbiiGdpr.consentsChange').subscribe(({ WizbiiGdpr: { consent } }) => {
      ctx.dispatch(new SetConsents(consent));
    });
    // Listen for a consent changed on its own
    fromEvent(this.document, 'WizbiiGdpr.consentChange').subscribe(({ WizbiiGdpr: { consent } }) => {
      ctx.dispatch(new SetConsent(consent));
    });
  }

  @Action(SetConsents)
  setConsents(ctx: StateContext<ConsentsStateModel>, { consents }: SetConsents): void {
    const keyTest = Object.keys(consents)[0];

    if (Array.isArray(consents)) {
      ctx.setState(consents);
    } else if (Array.isArray(consents[keyTest])) {
      ctx.setState(Object.values(consents).flat());
    } else {
      ctx.setState(Object.keys(consents).map((key) => ({ key, value: consents[key] })) as any);
    }
  }

  @Action(SetConsent)
  setConsent(ctx: StateContext<ConsentsStateModel>, { consent }: SetConsent): void {
    ctx.setState(
      iif(
        (state) => !!state.find((stateConsent) => stateConsent.key === consent.key),
        updateItem((stateConsent) => stateConsent.key === consent.key, consent),
        insertItem(consent)
      )
    );
  }
}
